import { Injectable } from '@angular/core';
import { LoggerService } from '../logger.service';
import { ERROR_CODES, ErrCode } from '../../shared/models';
import { Entry, FileEntry, FileSystemCallback } from '../../filewriter';
import { IStorageMethod, IStorageOpts, ITransferItemDownload, IRenderData } from 'src/sync';

declare global {
  interface Window {
    TEMPORARY: number;
    PERSISTENT: number;
  }
}

const TEMP_HEADROOM_MULTIPLIER = 2;
const SAVE_GRACE_BYTES_PER_MS = 20 * 1024;
const SAVE_GRACE_MIN_MS = 60 * 1000;
const SAVE_GRACE_MAX_MS = 60 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class StorageFilesystemService implements IStorageMethod {
  public maxsize = 0; // no maximum size

  private dirname = 'syncpreview';
  private options: { [key: string]: IStorageOpts } = {};

  constructor(private log: LoggerService) {}

  public async init(initOpts: IStorageOpts): Promise<void> {
    const key = `${initOpts.blobtype}-${initOpts.cachekey}`;
    this.dirname = initOpts.dirname;
    this.options[key] = {
      dirname: initOpts.dirname,
      filesize: initOpts.filesize,
      filename: initOpts.filename,
      cachekey: initOpts.cachekey,
      blobtype: initOpts.blobtype,
      mimetype: initOpts.mimetype,
      path: `${initOpts.dirname}/${key}`,
      storetype: await this.getSpace(initOpts.filesize),
    };
  }

  public async test(): Promise<void> {
    const key = 'btFILE-000test000';
    this.dirname = 'syncpreview';
    this.options[key] = {
      dirname: 'syncpreview',
      filesize: 1,
      filename: 'filename.jpg',
      cachekey: '000test000',
      blobtype: 'btFILE',
      mimetype: '',
      path: 'syncpreview/' + key,
      storetype: 0,
    };

    const storeType = await this.getSpace(this.options[key].filesize);
    this.options[key].storetype = storeType;
    await this.getFS(storeType, this.options[key]);
    const bytearray = [0];
    await this.writeChunk(this.options[key], bytearray, 0);
  }

  public async clearSpace(type: number): Promise<void> {
    const list: Entry[] = [];
    return await new Promise<void>((resolve, reject) => {
      this.queryUsageAndQuota(
        type,
        (used, remain) => {
          if (used + remain > 0) {
            this.getFileSystem(
              type,
              1024,
              (fs) => {
                fs.root.getDirectory(
                  this.dirname,
                  { create: true },
                  (dirEntry) => {
                    this.log.info('Clear space found root dir');
                    const dirReader = dirEntry.createReader();
                    dirReader.readEntries(
                      async (entries) => {
                        const len = entries.length;
                        if (!len || len == 0) {
                          this.log.info('The dir is empty');
                          resolve();
                        }
                        for (let i = 0; i < len; i++) {
                          const entry = entries[i];
                          if (entry.isFile) {
                            list.push(entry);
                          }
                        }
                        await this.deleteFiles(list).then(
                          () => resolve(),
                          (err) => reject(err),
                        );
                      },
                      (err) => reject(err),
                    );
                  },
                  (err) => reject(err),
                );
              },
              (err) => reject(err),
            );
          } else {
            this.log.info('Cleared all possible');
            resolve();
          }
        },
        (err) => reject(err),
      );
    });
  }

  public exists(tItem: ITransferItemDownload): Promise<never> {
    return Promise.reject();
  }

  public async getRenderData(tItem: ITransferItemDownload): Promise<IRenderData> {
    const key = `${tItem.blobtype}-${tItem.cachekey}`,
      opts = this.options[key];
    return await new Promise<IRenderData>((resolve, reject) => {
      this.getFileSystem(
        opts.storetype,
        tItem.filesize,
        (fs) => {
          fs.root.getFile(
            opts.path,
            {},
            (fileEntry) => {
              resolve({
                type: 'url',
                url: fileEntry.toURL(),
                blob: null,
              });
            },
            (err) => reject(err),
          );
        },
        (err) => reject(err),
      );
    });
  }

  public async writeChunk(
    tItem: ITransferItemDownload | IStorageOpts,
    bytearray: number[] | Uint8Array[],
    offset: number,
    hasCleaned?: boolean,
  ): Promise<unknown> {
    const time = Date.now(),
      key = `${tItem.blobtype}-${tItem.cachekey}`,
      opts = this.options[key];
    hasCleaned = !!hasCleaned;
    return await new Promise((resolve, reject) => {
      this.getFileSystem(
        opts.storetype,
        tItem.filesize,
        (fs) => {
          fs.root.getFile(
            opts.path,
            { create: true },
            (fileEntry) => {
              fileEntry.createWriter(
                (writer) => {
                  let truncated = false;
                  let blob: Blob;
                  writer.onwriteend = (): void => {
                    if (!truncated) {
                      const truncsize = tItem.blobtype != 'btFILE' ? tItem[tItem.blobtype + 'size'] : opts.filesize;
                      this.log.info(' - truncate - ' + truncsize);
                      truncated = true;
                      writer.truncate(truncsize);
                    }
                    this.log.info(`WRITE OP ${Date.now() - time} ms write data`);
                    blob = null;
                    resolve(true);
                  };
                  writer.onerror = (e): void | Promise<void> => {
                    this.log.info(writer.error.toString());
                    this.log.error(`Write failed ${writer.error.name}`);
                    if (!hasCleaned && writer.error.name == 'QuotaExceededError') {
                      this.log.info('Clearing space');
                      return this.clearSpace(opts.storetype)
                        .then(() => this.writeChunk(tItem, bytearray, offset, hasCleaned))
                        .then(resolve)
                        .catch(reject);
                    } else {
                      reject(new ErrCode(ERROR_CODES.BROWSER_FILE_WRITE_FAILED));
                    }
                  };
                  if (typeof bytearray == 'object' && bytearray instanceof Uint8Array) {
                    blob = new Blob([bytearray], { type: 'application/octet-binary' });
                  } else {
                    blob = new Blob(<any>bytearray, { type: 'application/octet-binary' });
                  }
                  if (tItem.blobtype != 'btFILE') {
                    tItem[tItem.blobtype + 'size'] = blob.size;
                  }
                  writer.seek(offset);
                  writer.write(blob);
                },
                (err) => {
                  this.log.error('Error creating a filewriter');
                  reject(new ErrCode(ERROR_CODES.BROWSER_FILE_WRITER_CREATE_FAILED));
                },
              );
            },
            (err) => {
              this.log.error('Error getting a file entry for ' + opts.path);
              this.log.error(err.toString());
              reject(new ErrCode(ERROR_CODES.BROWSER_FILE_ENTRY_MISSING));
            },
          );
        },
        (err) => reject(err),
      );
    });
  }

  /**
   * Recursively delete a file from the list.
   *
   * This will take an array of objects from the list and then shift off
   * items to delete them.  It will call itself again passing the deferred
   * object, which will only be resolved once completed.
   */
  private async deleteFiles(list: Entry[]): Promise<void> {
    return await new Promise<void>((resolve, reject) => {
      if (!list || !list.length) {
        resolve();
        return;
      }

      const entry = list.shift();
      if (!entry) {
        resolve();
        return;
      }
      entry.getMetadata(
        (meta) => {
          // a file may still be copying from FileSystem storage to the user's
          // disk, so hold off deleting for roughly as long as that copy takes
          const graceMs = Math.min(SAVE_GRACE_MAX_MS, +meta.size / SAVE_GRACE_BYTES_PER_MS + SAVE_GRACE_MIN_MS);
          const deleteTime = meta.modificationTime.getTime() + graceMs;
          if (deleteTime < Date.now()) {
            this.log.warn(`${entry.fullPath} attempt deletion`);
            entry.remove(
              () => {
                this.log.warn(`Deleted ${entry.fullPath}, size: ${meta.size}`);
                return this.deleteFiles(list).then(resolve).catch(reject);
              },
              (err) => reject(err),
            );
          } else {
            this.log.warn(`Skipping ${entry.fullPath} because it's too new`);
            return this.deleteFiles(list).then(resolve).catch(reject);
          }
        },
        (err) => reject(err),
      );
    });
  }

  private async getSpace(size: number, cleared?: ClearedPools): Promise<number> {
    const reqBytes = size,
      tempReqBytes = size * TEMP_HEADROOM_MULTIPLIER;
    const clearedPools: ClearedPools = cleared ?? { persistent: false, temporary: false };

    this.log.info(' Requesting disk space storage');
    this.log.info('   size: ' + size);
    this.log.info('   temp storage needed: ' + tempReqBytes);

    const quotas = await this.getUsage();
    this.log.info(' - Persistent used: ' + quotas.pUsed);
    this.log.info(' - Persistent remain: ' + quotas.pRemain);
    this.log.info(' - Persistent total: ' + quotas.pTotal);
    this.log.info(' - Temporary used: ' + quotas.tUsed);
    this.log.info(' - Temporary remain: ' + quotas.tRemain);
    this.log.info(' - Temporary total: ' + quotas.tTotal);

    if (quotas.pRemain >= reqBytes) {
      this.log.info('Persistent storage should accomodate this');
      return window.PERSISTENT;
    }
    if (quotas.tRemain >= tempReqBytes) {
      this.log.info('Temporary storage should accomodate this');
      return window.TEMPORARY;
    }
    if (quotas.pTotal >= reqBytes && !clearedPools.persistent) {
      this.log.info('Persistent could accomodate once cleared, clearing space');
      await this.clearSpace(window.PERSISTENT);
      return await this.getSpace(size, { ...clearedPools, persistent: true });
    }
    if (quotas.tTotal >= tempReqBytes && !clearedPools.temporary) {
      this.log.info('Temporary could accomodate once cleared, clearing space');
      await this.clearSpace(window.TEMPORARY);
      return await this.getSpace(size, { ...clearedPools, temporary: true });
    }
    this.log.info('Neither pool can accomodate, asking for 2^40 bytes persistent');
    return await this.requestQuota(reqBytes);
  }

  private async getUsage(): Promise<FileSystemUsageData> {
    return await new Promise<FileSystemUsageData>((resolve, reject) => {
      this.queryUsageAndQuota(
        window.TEMPORARY,
        (tUsed, tRemain) => {
          this.queryUsageAndQuota(
            window.PERSISTENT,
            (pUsed, pRemain) => {
              resolve({
                pUsed: pUsed,
                pRemain: pRemain,
                pTotal: pUsed + pRemain,
                tUsed: tUsed,
                tRemain: tRemain,
                tTotal: tUsed + tRemain,
              });
            },
            (err) => reject(err),
          );
        },
        (err) => reject(err),
      );
    });
  }

  private async getFS(type: number, initOpts: IStorageOpts): Promise<FileEntry> {
    return await new Promise<FileEntry>((resolve, reject) => {
      this.log.d('attempting getFS for type = ' + type);
      this.log.d(initOpts);
      this.getFileSystem(
        type,
        initOpts.filesize,
        (fs) => {
          this.log.d('getFileSystem returned success');
          fs.root.getDirectory(
            initOpts.dirname,
            { create: true },
            () => {
              fs.root.getFile(
                initOpts.path,
                { create: true },
                (res) => {
                  resolve(res);
                },
                (err) => {
                  this.log.e('reject getFS', err);
                  reject(err);
                },
              );
            },
            (err) => {
              this.log.e('reject getDirectory', err);
              reject(err);
            },
          );
        },
        (err) => {
          this.log.e('Error getting filesystem', err);
          reject(err);
        },
      );
    });
  }

  private getFileSystem(
    type: number,
    size: number,
    successCallback: FileSystemCallback,
    errorCallback?: StorageErrorCallback,
  ): void {
    if (window.webkitRequestFileSystem) {
      window.webkitRequestFileSystem(type, size, successCallback, errorCallback);
    } else if (window.requestFileSystem) {
      window.requestFileSystem(type, size, successCallback, errorCallback);
    } else {
      this.log.error('requestFileSystem does not exist on window');
      if (errorCallback) {
        errorCallback(new ErrCode(ERROR_CODES.BROWSER_UNSUPPORTED_DOWNLOAD));
      }
    }
  }

  private queryUsageAndQuota(
    type: number,
    successCallback: StorageUsageCallback,
    errorCallback: StorageErrorCallback,
  ): void {
    const poolName = type == window.TEMPORARY ? 'Temporary' : 'Persistent';
    const navigatorKey = `webkit${poolName}Storage`;
    if (navigator[navigatorKey]) {
      return navigator[navigatorKey].queryUsageAndQuota(successCallback, errorCallback);
    } else if (window.webkitStorageInfo) {
      return window.webkitStorageInfo.queryUsageAndQuota(type, successCallback, errorCallback);
    } else {
      this.log.error('queryUsageAndQuota: no methods found');
      errorCallback(new ErrCode(ERROR_CODES.BROWSER_UNSUPPORTED_DOWNLOAD));
    }
  }

  private async requestQuota(reqBytes: number): Promise<number> {
    const size = 1099511627776;
    return await new Promise<number>((resolve, reject) => {
      const successCallback = (grantedBytes: number): void => {
        if (grantedBytes === 0) {
          this.log.error('We were granted 0 bytes');
          reject(new ErrCode(ERROR_CODES.BROWSER_DISK_SPACE));
        } else if (reqBytes > grantedBytes) {
          this.log.error('The requested byets are greater than the granted byets');
          this.log.error(`Asked for ${reqBytes}, granted ${grantedBytes} `);
          reject(new ErrCode(ERROR_CODES.BROWSER_DISK_SPACE));
        } else {
          this.log.info('PERSISTENT storage chosen');
          resolve(window.PERSISTENT);
        }
      };
      const errorCallback = (err: ErrCode | Error | DOMException): void => {
        reject(err);
      };
      if (navigator['webkitPersistentStorage']) {
        navigator['webkitPersistentStorage'].requestQuota(size, successCallback, errorCallback);
      } else if (window.webkitStorageInfo) {
        window.webkitStorageInfo.requestQuota(window.PERSISTENT, size, successCallback, errorCallback);
      } else {
        this.log.error('requestQuota: no methods found');
        reject(new ErrCode(ERROR_CODES.BROWSER_UNSUPPORTED_DOWNLOAD));
      }
    });
  }
}

type StorageUsageCallback = (used: number, remain: number) => void;

type StorageErrorCallback = (err: ErrCode | Error | DOMException) => void;

interface ClearedPools {
  persistent: boolean;
  temporary: boolean;
}

interface FileSystemUsageData {
  pUsed: number;
  pRemain: number;
  pTotal: number;
  tUsed: number;
  tRemain: number;
  tTotal: number;
}
